// CREATED BY SREERAJ 24-10-2025
//FOR REPORT EXPORT FUNCTIONALITY AS REUSABLE HOOK

import { useState } from "react";
import * as XLSX from "xlsx";
import jsPDF from "jspdf";
import "jspdf-autotable";
import { useDispatch } from "react-redux";
import { closeExportLoader, openExportLoader } from "../features/slices/exportSlice";

/**
 * Reusable hook for handling Excel & PDF exports
 * @param {Function} fetchApiFn - API function to call for fetching export data
 * @param {Function} mapDataFn - Function to map response data into exportable rows
 * @param {Array} headers - Table headers (array of strings)
 * @param {String} title - Report title
 * @returns {Object} - Export handlers and loading states
 */
const useDataExport = (fetchApiFn, mapDataFn, headers, title = "Report") => {
    const dispatch = useDispatch();

    const [isExportingExcel, setIsExportingExcel] = useState(false);
    const [isExportingPDF, setIsExportingPDF] = useState(false);

    const handleExportExcel = async (filterValues, pagination = {}) => {
        try {
            setIsExportingExcel(true);
            dispatch(openExportLoader({ type: "excel", message: `Exporting ${title}...` }));
            const { page = 1, pageItemCount = 25 } = pagination;

            const formData = new FormData();
            formData.append("page", page);
            formData.append("pageItemcount", pageItemCount);
            Object.keys(filterValues).forEach((key) =>
                formData.append(key, filterValues[key])
            );

            const response = await fetchApiFn(formData);
            if (response?.status !== "success") {
                alert("No data available to export.");
                return;
            }

            const exportData = response.data?.data || [];
            if (exportData.length === 0) {
                alert("No data available to export.");
                return;
            }

            // Prepare data rows using custom mapper
            const tableData = exportData.map(mapDataFn);

            const sheetData = [
                [title],
                [],
                headers,
                ...tableData,
            ];

            const worksheet = XLSX.utils.aoa_to_sheet(sheetData);
            worksheet["!merges"] = [
                { s: { r: 0, c: 0 }, e: { r: 0, c: headers.length - 1 } },
            ];
            worksheet["A1"].s = {
                font: { bold: true, sz: 18 },
                alignment: { horizontal: "center" },
            };
            worksheet["!cols"] = headers.map(() => ({ wch: 20 }));

            const workbook = XLSX.utils.book_new();
            XLSX.utils.book_append_sheet(workbook, worksheet, title);
            XLSX.writeFile(workbook, `${title.replace(/\s+/g, "_")}.xlsx`);
        } catch (error) {
            console.error("Excel export failed:", error);
        } finally {
            setIsExportingExcel(false);
            dispatch(closeExportLoader());
        }
    };

    const handleExportPDF = async (filterValues, pagination = {}) => {
        try {
            setIsExportingPDF(true);
            dispatch(openExportLoader({ type: "pdf", message: `Exporting ${title}...` }));

            const { page = 1, pageItemCount = 25 } = pagination;

            const formData = new FormData();
            formData.append("page", page);
            formData.append("pageItemcount", pageItemCount);
            Object.keys(filterValues).forEach((key) =>
                formData.append(key, filterValues[key])
            );

            const response = await fetchApiFn(formData);
            if (response?.status !== "success") {
                alert("No data available to export.");
                return;
            }

            const exportData = response.data?.data || [];
            if (exportData.length === 0) {
                alert("No data available to export.");
                return;
            }

            const doc = new jsPDF({ orientation: "landscape" });
            doc.setFontSize(16);
            doc.setFont("helvetica", "bold");
            doc.text(title, doc.internal.pageSize.getWidth() / 2, 15, {
                align: "center",
            });

            const tableRows = exportData.map(mapDataFn);

            doc.autoTable({
                head: [headers],
                body: tableRows,
                startY: 25,
                styles: { fontSize: 9, cellPadding: 3, textColor: 40 },
                headStyles: {
                    fillColor: [180, 180, 180],
                    textColor: 20,
                    halign: "center",
                    fontStyle: "bold",
                },
                bodyStyles: { halign: "center", valign: "middle", textColor: 30 },
                alternateRowStyles: { fillColor: [230, 230, 230] },
                margin: { top: 25 },
                didDrawPage: () => {
                    const pageCount = doc.internal.getNumberOfPages();
                    const pageText = `Page ${doc.internal.getCurrentPageInfo().pageNumber} of ${pageCount}`;
                    doc.setFontSize(10);
                    doc.text(
                        pageText,
                        doc.internal.pageSize.getWidth() / 2,
                        doc.internal.pageSize.getHeight() - 10,
                        { align: "center" }
                    );
                },
            });

            doc.save(`${title.replace(/\s+/g, "_")}.pdf`);
        } catch (error) {
            console.error("PDF export failed:", error);
        } finally {
            setIsExportingPDF(false);
            dispatch(closeExportLoader());
        }
    };

    return {
        handleExportExcel,
        handleExportPDF,
        isExportingExcel,
        isExportingPDF,
    };
};

export default useDataExport;
